import React, { useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { MASTER_ADMIN_MENU_REGISTRY } from '../../config/adminMenuRegistry';

const GlobalSearch = forwardRef((props, ref) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const flatResultsRef = useRef([]);

  // Expose focus method to parent
  useImperativeHandle(ref, () => ({
    focus: () => {
      setSearchTerm('');
      setSelectedIndex(0);
      setIsOpen(true);
      // Use rAF + setTimeout to ensure DOM is updated before focusing
      requestAnimationFrame(() => {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 50);
      });
    },
    isOpen: () => isOpen
  }), [isOpen]);

  // Group and filter results with instant search (200ms debounce)
  // When empty: show ALL pages (F2 list mode). When typing: filter.
  const groupedResults = useMemo(() => {
    const searchLower = searchTerm.trim().toLowerCase();
    const filtered = searchLower
      ? MASTER_ADMIN_MENU_REGISTRY.filter(item => {
          return (
            item.label.toLowerCase().includes(searchLower) ||
            item.section.toLowerCase().includes(searchLower) ||
            item.keywords.some(keyword => keyword.toLowerCase().includes(searchLower))
          );
        })
      : MASTER_ADMIN_MENU_REGISTRY;

    // Group by section
    const grouped = filtered.reduce((acc, item) => {
      if (!acc[item.section]) {
        acc[item.section] = [];
      }
      acc[item.section].push(item);
      return acc;
    }, {});

    // Create flat array for keyboard navigation
    flatResultsRef.current = [];
    Object.keys(grouped).forEach(section => {
      grouped[section].forEach(item => {
        flatResultsRef.current.push({ ...item, section });
      });
    });

    return grouped;
  }, [searchTerm]);

  // Instant search with 200ms debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      // Results are already computed with useMemo
      setSelectedIndex(0);
    }, 200);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Enhanced keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen && e.key === 'ArrowDown') {
      setIsOpen(true);
      return;
    }

    const totalResults = flatResultsRef.current.length;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % totalResults);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + totalResults) % totalResults);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (totalResults > 0) {
        handleSelect(flatResultsRef.current[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleSelect = (item) => {
    setIsOpen(false);
    setSearchTerm('');
    setSelectedIndex(0);
    inputRef.current?.blur();
    navigate(item.route);
  };

  const getIcon = (iconName) => {
    const IconComponent = Icons[iconName] || Icons.FileText;
    return <IconComponent className="w-4 h-4" />;
  };

  // Get section icon
  const getSectionIcon = (section) => {
    const sectionIcons = {
      'Core': Icons.LayoutDashboard,
      'Send SMS': Icons.Send,
      'User Management': Icons.Users,
      'DLT': Icons.FileCheck,
      'Plans': Icons.Package,
      'Reports': Icons.BarChart3,
      'Security': Icons.ShieldCheck,
      'SMPP': Icons.Server,
      'Platform Config': Icons.Settings2,
      'Monitoring': Icons.Activity,
      'Workers': Icons.Cpu,
      'Settings': Icons.Settings,
      'Migration': Icons.ArrowRightLeft,
      'Documentation': Icons.BookOpen,
      'Support': Icons.HelpCircle,
      'WhatsApp': Icons.MessageCircle,
      'Global': Icons.Globe,
    };
    const IconComponent = sectionIcons[section] || Icons.Folder;
    return <IconComponent className="w-4 h-4" />;
  };

  const hasResults = Object.keys(groupedResults).length > 0;

  return (
    <div ref={searchRef} className="relative">
      {/* Search Input */}
      <div className="ui-search" style={{ width: '380px' }}>
        <span className="ui-search-icon">🔍</span>
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search menu… (F2 to open, ↑↓ to navigate, ⏎ to select)"
          className="ui-search-input"
        />
      </div>

      {/* Dropdown Results */}
      {isOpen && (
        <div className="absolute top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[520px] overflow-y-auto" style={{ minWidth: '420px' }}>
          {isOpen && hasResults ? (
            <>
              {Object.entries(groupedResults).map(([section, items], sectionIndex) => {
                // Track the global index for keyboard navigation
                let globalIndex = 0;
                Object.entries(groupedResults).slice(0, sectionIndex).forEach(([_, prevItems]) => {
                  globalIndex += prevItems.length;
                });

                return (
                  <div key={section} className="mb-2 last:mb-0">
                    {/* Section Header */}
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                      {getSectionIcon(section)}
                      <span>{section}</span>
                      <span className="ml-auto text-gray-400">({items.length})</span>
                    </div>
                    
                    {/* Section Items */}
                    {items.map((item, itemIndex) => {
                      const currentIndex = globalIndex + itemIndex;
                      const isSelected = currentIndex === selectedIndex;
                      
                      return (
                        <button
                          key={item.route}
                          onClick={() => handleSelect(item)}
                          className={`w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-b-0 ${
                            isSelected ? 'bg-indigo-50 border-l-2 border-indigo-500' : ''
                          }`}
                        >
                          <div className="text-gray-400 flex-shrink-0">
                            {getIcon(item.icon || 'FileText')}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">
                              {item.label}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {item.route}
                            </div>
                          </div>
                          {isSelected && (
                            <Icons.ChevronRight className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
              
              {/* Keyboard Shortcut Hint */}
              <div className="px-4 py-2 text-xs text-gray-400 bg-gray-50 border-t border-gray-100 flex items-center justify-center gap-4">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs">F2</kbd>
                  Open
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs">↑↓</kbd>
                  Navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs">⏎</kbd>
                  Select
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs">ESC</kbd>
                  Close
                </span>
              </div>
            </>
          ) : searchTerm.trim() ? (
            /* No Results State */
            <div className="px-4 py-8 text-center">
              <Icons.Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <div className="text-sm text-gray-500 font-medium">No menu items found</div>
              <div className="text-xs text-gray-400 mt-1">Try searching with different keywords</div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
});

export default GlobalSearch;
